import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './ItemDetail.css';
import { urlApiServerImage } from '../../../Config/Config';

const getOrientation = () =>
  window.matchMedia('(orientation: portrait)').matches ? 'portrait' : 'landscape';

const useOrientation = () => {
  const [orientation, setOrientation] = useState(getOrientation());

  useEffect(() => {
    const mediaQuery = window.matchMedia('(orientation: portrait)');
    const handler = () => setOrientation(getOrientation());
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  return orientation;
};

const ItemDetail = ({ item = null, windowHeight }) => {
  // Proporcionar un objeto vacío por defecto para item
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const safeItem = item || {
    galeria: [],
    logos_izquierda: null,
    logos_derecha: [],
    caracteristicas: [],
    color_principals: '',
    color_secundarios: '',
    Nombre: '',
    introduccion: ''
  };

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const containerRef = useRef(null);
  const wheelTimeoutRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const orientation = useOrientation();
  const isPortrait = orientation === 'portrait';
  const isNavigating = useRef(false);

  // Calcular el índice máximo con protección
  const maxGalleryIndex = (safeItem?.imagenes_linea?.length || 0) + 1;

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [safeItem]);

  // Función segura para extraer colores


  const handleWheel = (e) => {
    if (isNavigating.current) return;
    e.preventDefault();
    if (wheelTimeoutRef.current) clearTimeout(wheelTimeoutRef.current);
    const isScrollingDown = e.deltaY > 0;
    setCurrentImageIndex(prev => {
      const next = isScrollingDown
        ? Math.min(prev + 1, maxGalleryIndex)
        : Math.max(prev - 1, 0);
      return next;
    });
    wheelTimeoutRef.current = setTimeout(() => { }, 300);
  };

  const handleTouchStart = (e) => {
    if (isNavigating.current) return;
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    if (isNavigating.current) return;
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (isNavigating.current) {
      isNavigating.current = false;
      return;
    }

    const diff = touchStartX.current - touchEndX.current;
    const swipeThreshold = 50;
    if (diff > swipeThreshold) {
      setCurrentImageIndex(prev => Math.min(prev + 1, maxGalleryIndex));
    } else if (diff < -swipeThreshold) {
      setCurrentImageIndex(prev => Math.max(prev - 1, 0));
    }
    touchStartX.current = 0;
    touchEndX.current = 0;
  };

  const navigateImage = (direction) => {
    if (isNavigating.current) return;
    isNavigating.current = true;

    if (direction === 'prev') {
      setCurrentImageIndex(prev => Math.max(prev - 1, 0));
    } else {
      setCurrentImageIndex(prev => Math.min(prev + 1, maxGalleryIndex));
    }

    setTimeout(() => {
      isNavigating.current = false;
    }, 500);
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', handleWheel);
      if (wheelTimeoutRef.current) clearTimeout(wheelTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxGalleryIndex]);

  const renderLeftLogos = () => {
    const logosRaw = safeItem?.logos_izquierda;

    // Si no hay logos, retornar null para desactivar el contenedor
    if (!logosRaw || !logosRaw.url) return null;

    return (
      <div className={`left-logo-single ${currentImageIndex > 1 ? 'enlarged-logo' : ''}`}>
        <motion.img
          src={urlApiServerImage + logosRaw.url}
          alt="Logo"
          className={`left-logo-img ${isPortrait ? 'portrait-padding' : 'landscape-padding'}`}
          whileHover={{ scale: 1.05 }}
        />
      </div>
    );
  };

  const renderRightLogos = () => {
    const logos = safeItem?.logos_derecha || [];
    if (logos.length === 0) return null;
    return (
      <div className={`right-logos-container ${isPortrait ? 'portrait-layout' : 'landscape-layout'}`}>
        {logos.map((logo, index) => (
          <motion.div
            key={index}
            className={`right-logo-item ${isPortrait ? 'portrait-size' : 'landscape-size'}`}
            whileHover={{ scale: 1.03 }}
          >
            <img
              src={urlApiServerImage + (logo?.url || '')}
              alt={`Logo ${index + 1}`}
              className="right-logo-img"
            />
          </motion.div>
        ))}
      </div>
    );
  };

  // Renderizar características con protección
  const renderFeatures = () => {
    if (!safeItem?.caracteristicas || safeItem.caracteristicas.length === 0) {
      return <p>No hay características disponibles</p>;
    }

    return safeItem.caracteristicas.flatMap((node, index) => {
      if (!node) return [];

      if (node.type === "paragraph") {
        const text = node?.children?.[0]?.text?.trim();
        if (!text) return [];

        if (text.startsWith("•")) {
          return <li key={index}>{text.replace("•", "").trim()}</li>;
        }

        return (
          <p key={index} style={{ listStyle: "none", paddingBottom: "1rem" }}>
            {text}
          </p>
        );
      }

      if (node.type === "list") {
        return (node.children || []).map((listItem, subIndex) => {
          const text = listItem?.children?.[0]?.text?.trim();
          if (!text) return null;

          return <li key={`${index}-${subIndex}`}>{text}</li>;
        });
      }

      return null;
    });
  };

  return (
    <div
      ref={containerRef}
      className="item-detail-container"
      style={isPortrait ? { height: `calc(${windowHeight}px - 23vh)` } : { height: '100%' }}
    >
      <div className={`main-content ${isPortrait ? 'portrait-main' : 'landscape-main'}`}>
        {/* <div
          className={`left-section ${isPortrait ? 'portrait-left' : 'landscape-left'} ${currentImageIndex === 0 ? '' : 'secondary-bg'}`}
          style={{ 
            backgroundColor: currentImageIndex === 0 ? '#333b47' : safeItem?.color_secundario?.codigo_hexadecimal,
            height: isPortrait && currentImageIndex === 1 ? '70%' : isPortrait ? '60%' : '100%'
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        > */}

          <div
          className={`left-section ${isPortrait ? 'portrait-left' : 'landscape-left'} ${currentImageIndex === 0 ? '' : 'secondary-bg'}`}
          style={{
            backgroundColor: currentImageIndex === 0 ? '#333b47' : currentImageIndex === 1 ? safeItem?.color_principals?.[0]?.codigo_hexadecimal:'#3E4245' , // Cambiado a color fijo
            height: isPortrait && currentImageIndex === 1 ? '70%' : isPortrait ? '60%' : '100%'
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >

          <AnimatePresence mode="wait">
            <motion.div
              key={`left-${safeItem?.id || 'empty'}-${currentImageIndex}`}
              initial={{ opacity: 0, x: currentImageIndex === 0 ? 0 : 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: currentImageIndex === 0 ? 0 : -50 }}
              transition={{ duration: 0.3 }}
              className="left-content-wrapper"
            >
              {currentImageIndex === 0 ? (
                <div className="left-text-content">
                  <div className="nosotros-section">
                    <h2>{safeItem?.Nombre || ''}</h2>
                    <p>{safeItem?.introduccion || ''}</p>
                  </div>
                </div>
              ) : currentImageIndex === 1 ? renderLeftLogos() : (
                <div className="image-container">
                  {safeItem?.imagenes_linea?.[currentImageIndex - 2]?.Imagen?.url && (
                    <motion.img
                      src={urlApiServerImage + safeItem.imagenes_linea[currentImageIndex - 2]?.Imagen?.url}
                      alt="Imagen"
                      className="detail-image"
                      whileHover={{ scale: 1.03 }}
                    />
                  )}
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {isPortrait && (
            <div className="navigation-buttons">
              <button
                onClick={() => navigateImage('prev')}
                onTouchStart={(e) => {
                  e.stopPropagation();
                  navigateImage('prev');
                }}
                disabled={currentImageIndex === 0 || isNavigating.current}
                className={`nav-button ${currentImageIndex === 0 ? 'disabled-button' : 'active-button'}`}
              >
                ←
              </button>
              <button
                onClick={() => navigateImage('next')}
                onTouchStart={(e) => {
                  e.stopPropagation();
                  navigateImage('next');
                }}
                disabled={currentImageIndex === maxGalleryIndex || isNavigating.current}
                className={`nav-button ${currentImageIndex === maxGalleryIndex ? 'disabled-button' : 'active-button'}`}
              >
                →
              </button>
            </div>
          )}
        </div>

        <div
          className={`right-section ${isPortrait ? 'portrait-right' : 'landscape-right'}`}
          style={{ backgroundColor: currentImageIndex === 0 ? '#202835' : currentImageIndex >= 2 ? safeItem?.color_principals?.[0]?.codigo_hexadecimal : safeItem?.color_secundarios?.[0]?.codigo_hexadecimal }}
        >
          {currentImageIndex === 0 ? (
            <div className="right-text-content">
              <div className="caracteristicas-section">
                <h2>Características</h2>
                <ul>{renderFeatures()}</ul>
              </div>
            </div>
          ) : currentImageIndex === 1 ? (
            <AnimatePresence mode="wait">
              <motion.div
                key={`right-logos-${safeItem?.id || 'empty'}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="right-content-wrapper"
                style={{
                  height: '100%'
                }}
              >
                {renderRightLogos()}
              </motion.div>
            </AnimatePresence>
          ) : (
            <div className={`right-content ${isPortrait ? 'portrait-content' : 'landscape-content'}`}>
              {(safeItem?.imagenes_linea?.[currentImageIndex - 2]?.nombre || safeItem?.imagenes_linea?.[currentImageIndex - 2]?.orientacion) ? (
                <div className={`text-content ${isPortrait ? 'portrait-text' : 'landscape-text'}`}>
                  <h2 className="item-title">
                    {safeItem?.imagenes_linea?.[currentImageIndex - 2]?.nombre || ''}
                  </h2>
                  <p className="item-description">
                    {safeItem?.imagenes_linea?.[currentImageIndex - 2]?.orientacion || ''}
                  </p>
                </div>
              ) : null}
              <div className={`logo-content ${isPortrait ? 'portrait-logo' : 'landscape-logo'} ${currentImageIndex > 1 ? 'enlarged-right-logo' : ''}`}>
                {Array.isArray(safeItem?.logos_derecha) && safeItem.logos_derecha.length > 0 && (
                  <motion.img
                    src={urlApiServerImage + (safeItem.logos_derecha[0]?.url || '')}
                    alt="Logo"
                    className="right-logo"
                    whileHover={{ scale: 1.02 }}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="indicator-container">
        <div
          className={`indicator ${currentImageIndex === 0 ? 'active-indicator' : 'inactive-indicator'}`}
          onClick={() => {
            if (!isNavigating.current) {
              isNavigating.current = true;
              setCurrentImageIndex(0);
              setTimeout(() => { isNavigating.current = false; }, 500);
            }
          }}
        />
        <div
          className={`indicator ${currentImageIndex === 1 ? 'active-indicator' : 'inactive-indicator'}`}
          onClick={() => {
            if (!isNavigating.current) {
              isNavigating.current = true;
              setCurrentImageIndex(1);
              setTimeout(() => { isNavigating.current = false; }, 500);
            }
          }}
        />
        {(safeItem?.imagenes_linea || []).map((_, index) => (
          <div
            key={index}
            className={`indicator ${index === currentImageIndex - 2 ? 'active-indicator' : 'inactive-indicator'}`}
            onClick={() => {
              if (!isNavigating.current) {
                isNavigating.current = true;
                setCurrentImageIndex(index + 2);
                setTimeout(() => { isNavigating.current = false; }, 500);
              }
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default ItemDetail;